import { DecisionKindEnum } from "@/enum/decision-kind";
import { toast } from "@/components/ui/use-toast";
import { PayrollVoucherStatusEnum } from "@/enum/payroll-voucher-status";
import {
  useDecidePayrollVoucherBatchMutation,
  useDecidePayrollVoucherMutation,
  useGetPayrollVoucherCycleQuery,
  useGetPayrollVouchersQuery,
  type DecidePayrollVoucherBatchItem,
  type PayrollVoucherBatchResult,
  type PayrollVoucherListParams,
} from "@/services/payroll-voucher";
import { getUserFromLocalStorage } from "@/utils/getUserFromLocalStorage";
import { useCallback, useEffect, useMemo, useRef } from "react";
import {
  DEFAULT_VOUCHER_PER_PAGE,
  SortDirection,
  VoucherSortColumn,
} from "../constants";
import { decidedElsewhereSince } from "../decided-elsewhere";
import { PAYROLL_VOUCHER_TOASTS } from "../messages";
import { summarizeSkipped } from "../utils/batch-preflight";

import {
  CycleSummary,
  DateRange,
  PayrollVoucher,
  VoucherDecision,
} from "../types";
import { parsePayrollVoucherError } from "./payroll-voucher-error";

export interface VouchersPageMeta {
  total: number;
  lastPage: number;
  currentPage: number;
  perPage: number;
}

export interface VouchersSource {
  vouchers: readonly PayrollVoucher[];
  meta: VouchersPageMeta;
  cycle: CycleSummary;
  isLoading: boolean;
  isFetching: boolean;
  decide: (
    voucher: PayrollVoucher,
    decision: VoucherDecision,
  ) => Promise<boolean>;
  decideBatch: (
    vouchers: readonly PayrollVoucher[],
    decision: VoucherDecision,
  ) => Promise<boolean>;
}

const EMPTY_META: VouchersPageMeta = {
  total: 0,
  lastPage: 1,
  currentPage: 1,
  perPage: DEFAULT_VOUCHER_PER_PAGE,
};

const EMPTY_CYCLE: CycleSummary = {
  cycle: "",
  interval: "",
  days_to_close: 0,
  vouchers_in_cycle: 0,
  decided_percentage: 0,
  payroll_discount_in_cycle: 0,
  exported_percentage: 0,
};

const STATUS_BY_DECISION: Record<DecisionKindEnum, PayrollVoucherStatusEnum> = {
  [DecisionKindEnum.APPROVE]: PayrollVoucherStatusEnum.APPROVED,
  [DecisionKindEnum.REJECT]: PayrollVoucherStatusEnum.REJECTED,
};

export interface VouchersQuery {
  range: DateRange;
  page: number;
  perPage: number;
  status?: PayrollVoucherStatusEnum;
  sort?: VoucherSortColumn;
  direction?: SortDirection;
}

export const toListParams = (
  query: VouchersQuery,
): PayrollVoucherListParams => ({
  start: query.range.start,
  end: query.range.end,
  page: query.page,
  perPage: query.perPage,
  status: query.status,
  sort: query.sort,
  direction: query.direction,
});

const withNumericAmount = (voucher: PayrollVoucher): PayrollVoucher => ({
  ...voucher,
  amount: Number(voucher.amount),
});

export const useVouchersSource = (query: VouchersQuery): VouchersSource => {
  const {
    data: vouchersPage,
    isLoading: isLoadingVouchers,
    isFetching,
  } = useGetPayrollVouchersQuery(toListParams(query));

  const vouchers: readonly PayrollVoucher[] = useMemo(
    (): readonly PayrollVoucher[] =>
      (vouchersPage?.data ?? []).map(withNumericAmount),
    [vouchersPage],
  );

  const previousVouchersRef = useRef<readonly PayrollVoucher[]>(vouchers);

  useEffect((): void => {
    const currentUserEmail: string | undefined =
      getUserFromLocalStorage()?.email;

    decidedElsewhereSince(previousVouchersRef.current, vouchers).forEach(
      (decided: PayrollVoucher): void => {
        if (decided.decided_by_email === currentUserEmail) return;

        toast(
          PAYROLL_VOUCHER_TOASTS.DECIDED_ELSEWHERE(
            decided.employee_name,
            decided.decided_by_email ?? "",
            decided.status === PayrollVoucherStatusEnum.APPROVED,
          ),
        );
      },
    );

    previousVouchersRef.current = vouchers;
  }, [vouchers]);

  const { data: cycle, isLoading: isLoadingCycle } =
    useGetPayrollVoucherCycleQuery();

  const [decidePayrollVoucher] = useDecidePayrollVoucherMutation();

  const [decidePayrollVoucherBatch] = useDecidePayrollVoucherBatchMutation();

  const decide = useCallback(
    async (
      voucher: PayrollVoucher,
      decision: VoucherDecision,
    ): Promise<boolean> => {
      const status: PayrollVoucherStatusEnum =
        STATUS_BY_DECISION[decision.kind];

      try {
        await decidePayrollVoucher({
          id: voucher.id,
          body: {
            status,
            version: voucher.version,
            decision_comment: decision.comment,
            rejection_reason: decision.rejection_reason,
          },
        }).unwrap();

        toast(
          PAYROLL_VOUCHER_TOASTS.DECISION_CONFIRMED(
            voucher.employee_name,
            status === PayrollVoucherStatusEnum.APPROVED,
          ),
        );

        return true;
      } catch (error: unknown) {
        toast(
          PAYROLL_VOUCHER_TOASTS.DECISION_FAILED(
            parsePayrollVoucherError(error).description,
          ),
        );

        return false;
      }
    },
    [decidePayrollVoucher],
  );

  const decideBatch = useCallback(
    async (
      selected: readonly PayrollVoucher[],
      decision: VoucherDecision,
    ): Promise<boolean> => {
      const status: PayrollVoucherStatusEnum =
        STATUS_BY_DECISION[decision.kind];

      const approved: boolean = status === PayrollVoucherStatusEnum.APPROVED;

      const items: DecidePayrollVoucherBatchItem[] = selected.map(
        (voucher: PayrollVoucher): DecidePayrollVoucherBatchItem => ({
          id: voucher.id,
          status,
          version: voucher.version,
          decision_comment: decision.comment,
          rejection_reason: decision.rejection_reason,
        }),
      );

      try {
        const result: PayrollVoucherBatchResult =
          await decidePayrollVoucherBatch({ items }).unwrap();

        const skippedSummary: string = summarizeSkipped(result.skipped);

        if (result.updated.length === 0) {
          toast(PAYROLL_VOUCHER_TOASTS.BATCH_NOTHING_DONE(skippedSummary));
          return false;
        }

        toast(
          skippedSummary
            ? PAYROLL_VOUCHER_TOASTS.BATCH_PARTIAL(
                result.updated.length,
                skippedSummary,
                approved,
              )
            : PAYROLL_VOUCHER_TOASTS.BATCH_SUCCESS(
                result.updated.length,
                approved,
              ),
        );

        return true;
      } catch (error: unknown) {
        toast(
          PAYROLL_VOUCHER_TOASTS.DECISION_FAILED(
            parsePayrollVoucherError(error).description,
          ),
        );

        return false;
      }
    },
    [decidePayrollVoucherBatch],
  );

  return {
    vouchers,
    meta: vouchersPage?.meta
      ? {
          total: vouchersPage.meta.total,
          lastPage: vouchersPage.meta.lastPage,
          currentPage: vouchersPage.meta.currentPage,
          perPage: vouchersPage.meta.perPage,
        }
      : EMPTY_META,
    cycle: cycle ?? EMPTY_CYCLE,
    isLoading: isLoadingVouchers || isLoadingCycle,
    isFetching,
    decide,
    decideBatch,
  };
};
